import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
  LinearProgress,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import CloseIcon from '@mui/icons-material/Close';
import CelebrationIcon from '@mui/icons-material/Celebration';
import PrintIcon from '@mui/icons-material/Print';
import { motion, AnimatePresence } from 'framer-motion';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: '16px',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    maxWidth: '500px',
  },
  dialogTitle: {
    background: 'rgba(255, 255, 255, 0.95)',
    padding: '24px',
    textAlign: 'center',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    right: 8,
    top: 8,
  },
  content: {
    padding: '32px 24px',
    textAlign: 'center',
    background: 'rgba(255, 255, 255, 0.95)',
  },
  greeting: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: 'rgba(224, 0, 0, 0.95)',
    marginBottom: '24px',
    textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
  },
  diceContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    margin: '32px 0',
    minHeight: '120px',
  },
  dice: {
    width: '100px',
    height: '100px',
    background: 'white',
    borderRadius: '15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    fontWeight: 'bold',
    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
    border: '3px solid #667eea',
    position: 'relative',
    transformStyle: 'preserve-3d',
  },
  rollingDice: {
    animation: '$roll 0.6s ease-in-out',
  },
  '@keyframes roll': {
    '0%': {
      transform: 'rotateX(0deg) rotateY(0deg)',
    },
    '50%': {
      transform: 'rotateX(360deg) rotateY(180deg)',
    },
    '100%': {
      transform: 'rotateX(720deg) rotateY(360deg)',
    },
  },
  rollButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '16px 48px',
    fontSize: '18px',
    fontWeight: 'bold',
    borderRadius: '50px',
    textTransform: 'none',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    '&:hover': {
      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
    },
    '&:disabled': {
      background: '#ccc',
      color: '#666',
    },
  },
  rollsInfo: {
    marginTop: '24px',
    marginBottom: '16px',
  },
  rollHistory: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: '16px',
  },
  rollCard: {
    padding: '12px 16px',
    background: '#f5f5f5',
    borderRadius: '8px',
    minWidth: '100px',
  },
  resultMessage: {
    marginTop: '24px',
    padding: '24px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    color: 'white',
    boxShadow: '0 4px 15px rgba(56, 239, 125, 0.3)',
  },
  winnerMessage: {
    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    boxShadow: '0 4px 15px rgba(245, 87, 108, 0.3)',
  },
  highestScore: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#667eea',
    marginTop: '16px',
  },
  diceDot: {
    width: '12px',
    height: '12px',
    background: '#667eea',
    borderRadius: '50%',
    position: 'absolute',
  },
  diceNumber: {
    fontSize: '56px',
    fontWeight: 'bold',
    color: '#667eea',
  }
}));

const DiceGameDialog = ({ open, onClose, userId, userName, userCompany }) => {
  const classes = useStyles();
  const [dice1, setDice1] = useState(1);
  const [dice2, setDice2] = useState(1);
  const [isRolling, setIsRolling] = useState(false);
  const [rollsLeft, setRollsLeft] = useState(3);
  const [rollHistory, setRollHistory] = useState([]);
  const [highestScore, setHighestScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isWinner, setIsWinner] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRollComplete = useCallback((finalDice1, finalDice2) => {
    const total = finalDice1 + finalDice2;
    
    // Update dice display values
    setDice1(finalDice1);
    setDice2(finalDice2);
    
    setRollHistory((prevHistory) => {
      const newRollHistory = [
        ...prevHistory,
        {
          rollNumber: 4 - rollsLeft,
          dice1: finalDice1,
          dice2: finalDice2,
          total,
          timestamp: new Date()
        }
      ];
      
      return newRollHistory;
    });
    
    setHighestScore((prevHighest) => Math.max(prevHighest, total));
    setRollsLeft((prev) => prev - 1);
    setIsRolling(false);
  }, [rollsLeft]);

  // Check if game is over when rolls left changes
  useEffect(() => {
    if (rollsLeft === 0 && rollHistory.length === 3) {
      finishGame(rollHistory, highestScore);
    }
  }, [rollsLeft, rollHistory, highestScore]);

  const rollDice = () => {
    if (isRolling || rollsLeft === 0) return;

    setIsRolling(true);

    // 2D dice animation for all browsers
    const rollDuration = 600;
    const rollInterval = setInterval(() => {
      setDice1(Math.floor(Math.random() * 6) + 1);
      setDice2(Math.floor(Math.random() * 6) + 1);
    }, 100);

    setTimeout(() => {
      clearInterval(rollInterval);
      // Generate final values after animation completes
      const finalDice1 = Math.floor(Math.random() * 6) + 1;
      const finalDice2 = Math.floor(Math.random() * 6) + 1;
      setDice1(finalDice1);
      setDice2(finalDice2);
      handleRollComplete(finalDice1, finalDice2);
    }, rollDuration);
  };

  const finishGame = async (rolls, finalHighestScore) => {
    setGameOver(true);
    setIsSubmitting(true);

    try {
      const diceGameApi = (await import('utils/api/diceGame')).default;
      const response = await diceGameApi.submit_dice_game(userId, rolls, finalHighestScore);
      
      if (response.data.success) {
        setIsWinner(response.data.isWinner);
      }
    } catch (error) {
      console.error('Error submitting dice game:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Allow closing anytime except when submitting or actively rolling
    if (!isSubmitting && !isRolling) {
      onClose();
    }
  };

  const printGameResult = () => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date().toLocaleString('en-US', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Dice Game Result - ${userName}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              max-width: 600px;
              margin: 0 auto;
              padding: 20px;
              background: #f5f5f5;
            }
            .header {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 30px;
              text-align: center;
              border-radius: 10px 10px 0 0;
              margin-bottom: 0;
            }
            .content {
              background: white;
              padding: 30px;
              border-radius: 0 0 10px 10px;
              box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            }
            .winner-badge {
              background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
              color: white;
              padding: 15px;
              border-radius: 8px;
              margin: 20px 0;
              text-align: center;
              font-size: 18px;
              font-weight: bold;
            }
            .game-details {
              margin: 20px 0;
              padding: 20px;
              background: #f8f9fa;
              border-radius: 8px;
              border-left: 4px solid #667eea;
            }
            .roll-history {
              margin: 20px 0;
            }
            .roll-item {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px solid #eee;
            }
            .roll-item:last-child {
              border-bottom: none;
            }
            .score {
              font-weight: bold;
              color: #667eea;
              font-size: 18px;
            }
            .footer {
              margin-top: 30px;
              padding: 20px;
              background: #e3f2fd;
              border-radius: 8px;
              text-align: center;
              font-size: 14px;
              color: #666;
            }
            @media print {
              body { background: white; }
              .header, .content, .footer { box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🎊 Congratulations! 🎊</h1>
            <h2>Dice Game Winner</h2>
          </div>

          <div class="content">
            <div class="winner-badge">
              🏆 WINNER CONFIRMED 🏆<br>
              ₱20 GCash Prize
            </div>

            <div class="game-details">
              <h3>Player Information</h3>
              <p><strong>Name:</strong> ${userName}</p>
              <p><strong>Company:</strong> ${userCompany}</p>
              <p><strong>Game Date:</strong> ${currentDate}</p>
              <p><strong>Final Score:</strong> <span class="score">${highestScore}</span></p>
            </div>

            <div class="roll-history">
              <h3>Roll History</h3>
              ${rollHistory.map((roll, index) => `
                <div class="roll-item">
                  <span>Roll ${roll.rollNumber}: ${roll.dice1} + ${roll.dice2}</span>
                  <span class="score">${roll.total}</span>
                </div>
              `).join('')}
            </div>

            <div style="text-align: center; margin-top: 30px; padding: 20px; background: #fff3cd; border-radius: 8px; border: 2px dashed #ffc107;">
              <strong>⚠️ Important:</strong><br>
              Please present this result to your HR department to claim your ₱20 GCash prize.<br>
              This serves as your official game result confirmation.
            </div>
          </div>

          <div class="footer">
            <p>This is an official game result from the Mid-Autumn Festival Dice Game.</p>
            <p>Generated on: ${currentDate}</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait a moment for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
      disableBackdropClick={isSubmitting || isRolling}
      disableEscapeKeyDown={isSubmitting || isRolling}
    >
      <DialogTitle className={classes.dialogTitle}>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          disabled={isSubmitting || (!gameOver && isRolling)}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent className={classes.content}>
        <Typography variant="h4" className={classes.greeting}>
          🎉 Happy Mid-Autumn Festival! 
        </Typography>

        <Typography variant="body1" style={{ marginBottom: '16px', color: '#666' }}>
          Roll the dice and try your luck!
          <br />
          <strong>Win condition:</strong> Score 11 or 12
          <br />
          <strong>Prize:</strong> ₱20 GCash
        </Typography>

        <Divider style={{ margin: '16px 0' }} />

        {/* Dice Display - 2D for all browsers */}
        <Box className={classes.diceContainer}>
          <motion.div
            className={classes.dice}
            animate={isRolling ? { rotateX: 360, rotateY: 360 } : {}}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
          >
            <span className={classes.diceNumber}>{dice1}</span>
          </motion.div>
          
          <motion.div
            className={classes.dice}
            animate={isRolling ? { rotateX: -360, rotateY: -360 } : {}}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
          >
            <span className={classes.diceNumber}>{dice2}</span>
          </motion.div>
        </Box>

        {/* Current Total */}
        {rollHistory.length > 0 && (
          <Typography variant="h5" style={{ color: '#667eea', fontWeight: 'bold', marginTop: '16px' }}>
            Last Roll: {rollHistory[rollHistory.length - 1].dice1} + {rollHistory[rollHistory.length - 1].dice2} = {rollHistory[rollHistory.length - 1].total}
          </Typography>
        )}

        {/* Roll Button */}
        {!gameOver && (
          <>
            <Box className={classes.rollsInfo}>
              <Typography variant="h6" style={{ color: '#764ba2', fontWeight: 'bold' }}>
                Rolls Left: {rollsLeft} / 3
              </Typography>
              {highestScore > 0 && (
                <Typography variant="body2" className={classes.highestScore}>
                  Highest Score: {highestScore}
                </Typography>
              )}
            </Box>

            <Button
              className={classes.rollButton}
              onClick={rollDice}
              disabled={isRolling || rollsLeft === 0}
              size="large"
            >
              {isRolling ? 'Rolling...' : 'Roll Dice 🎲'}
            </Button>
          </>
        )}

        {/* Roll History */}
        {rollHistory.length > 0 && (
          <Box className={classes.rollHistory}>
            {rollHistory.map((roll, index) => (
              <Paper key={index} className={classes.rollCard} elevation={2}>
                <Typography variant="caption" style={{ color: '#666' }}>
                  Roll {roll.rollNumber}
                </Typography>
                <Typography variant="h6" style={{ color: '#667eea', fontWeight: 'bold' }}>
                  {roll.total}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {/* Result Message */}
        {gameOver && !isSubmitting && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
          >
            <Box className={`${classes.resultMessage} ${isWinner ? classes.winnerMessage : ''}`}>
              {isWinner ? (
                <>
                  <CelebrationIcon style={{ fontSize: '48px', marginBottom: '12px' }} />
                  <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    🎊 Congratulations! 🎊
                  </Typography>
                  <Typography variant="body1">
                    You won ₱20 GCash!
                  </Typography>
                  <Typography variant="body2" style={{ marginTop: '12px' }}>
                    Please message your HR to claim your prize.
                  </Typography>
                  <Typography variant="h6" style={{ marginTop: '16px', fontWeight: 'bold' }}>
                    Your Score: {highestScore}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={printGameResult}
                    style={{
                      marginTop: '16px',
                      backgroundColor: '#fff',
                      color: '#667eea',
                      border: '2px solid #667eea',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      textTransform: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    Print Result 🖨️
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Thank you for playing! 🎲
                  </Typography>
                  <Typography variant="body1">
                    Better luck next time!
                  </Typography>
                  <Typography variant="h6" style={{ marginTop: '16px', fontWeight: 'bold' }}>
                    Your Best Score: {highestScore}
                  </Typography>
                </>
              )}
            </Box>
          </motion.div>
        )}

        {isSubmitting && (
          <Box style={{ marginTop: '24px' }}>
            <LinearProgress />
            <Typography variant="body2" style={{ marginTop: '8px', color: '#666' }}>
              Submitting your results...
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DiceGameDialog;

